import { FieldError } from '@calendly/form/parts';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { HideIcon } from '@calendly/ui/components/icons';
import { colorTextAction } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useEventTypeContext } from 'features/event_type_provider';
import { ColorPicker } from './color_picker';
import { useStateTrigger } from './state';
import { InputContainer } from './styles';
const styledTextFieldCss = "susov3m";
const StyledTextField = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'textarea';
  return <Component ref={ref} {...restProps} className={classNames(styledTextFieldCss, props.className)} />;
});
const iconWrapperCss = "i137cddu";
const IconWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconWrapperCss, props.className)} />;
});
const styledHeadingTextIsHoveredCss = "s1idl820";
const styledHeadingTextCss = "s1x07tsy";
const StyledHeadingText = forwardRef((props, ref) => {
  const {
    isHovered,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'h2';
  return <Component ref={ref} {...restProps} className={classNames(styledHeadingTextCss, {
    [styledHeadingTextIsHoveredCss]: isHovered
  }, props.className)} />;
});
const wrapperCss = "w1ix1fef";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const SecretIcon = () => <IconWrapper><HideIcon /></IconWrapper>;
const AutoResizingTextarea = forwardRef(({
  onChange,
  onKeyDown,
  ...rest
}, ref) => {
  const [value, setValue] = useState('');
  const handleChange = event => {
    setValue(event.target.value);
    if (onChange) onChange(event);
  };
  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
    if (onKeyDown) onKeyDown(event);
  };
  useEffect(() => {
    const textarea = ref.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 32), 32 * 4);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, ref]);
  return <StyledTextField ref={ref} onChange={handleChange} onKeyDown={handleKeyDown} rows="1" {...rest} />;
});
export const EditableEventType = ({
  colorPickerComponent,
  isSULCreation = false,
  nameError,
  nameValue,
  secret,
  setNameValue
}) => {
  const nameRef = useRef();
  const {
    isCreation: isETCreation
  } = useEventTypeContext();
  const isCreation = isETCreation || isSULCreation;
  const initialRef = useRef(true);
  const [isHovered, hover, unhover] = useBooleanState(false);
  const [isActive, activate, deactivate] = useBooleanState(false);
  const inputContainerRef = useRef(null);
  const {
    registerTriggerFunction
  } = useStateTrigger();
  const blur = useCallback(() => {
    deactivate();
    unhover();
  }, [deactivate, unhover]);
  const handleBlur = e => {
    if (!inputContainerRef.current.contains(e.relatedTarget)) {
      blur();
    }
  };
  const handleEnter = e => {
    if (e.key === 'Enter') {
      blur();
    }
  };
  const handleFocus = () => {
    if (nameRef.current) {
      const length = nameRef.current.value.length;
      nameRef.current.setSelectionRange(length, length);
      nameRef.current.focus();
    }
  };
  const handleInputContainerClick = useCallback(({
    shouldSelectText
  }) => {
    if (!isActive) {
      activate();
      setTimeout(() => {
        if (nameRef.current) {
          nameRef.current.focus();
          if (shouldSelectText) {
            nameRef.current.select();
          }
        }
      }, 0);
    }
  }, [isActive, activate, nameRef]);
  const handleKeyDown = useCallback(event => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleInputContainerClick({
        shouldSelectText: true
      });
    }
  }, [handleInputContainerClick]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
        blur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [blur]);
  useEffect(() => {
    if (isCreation && initialRef.current) {
      handleInputContainerClick({
        shouldSelectText: true
      });
      initialRef.current = false;
    }
  }, [isCreation, initialRef, handleInputContainerClick]);
  useEffect(() => {
    const handleTriggeredRename = () => {
      handleInputContainerClick({
        shouldSelectText: true
      });
    };
    if (registerTriggerFunction) registerTriggerFunction('rename', handleTriggeredRename);
  }, [registerTriggerFunction, handleInputContainerClick]);
  const ColorPickerComponent = colorPickerComponent || ColorPicker;
  return <Wrapper>
      <InputContainer ref={inputContainerRef} error={!!nameError} active={isActive} onClick={handleInputContainerClick} onMouseEnter={hover} onMouseLeave={unhover}>
        <ColorPickerComponent showChevron={isActive} onSelect={blur} />
        {isActive ? <AutoResizingTextarea id="event-name-field" name="name" aria-label="name" maxLength="56" placeholder={I18n.t('publisher.event_type_editor.sections.quickstart.name_your_event_label')} value={nameValue} onChange={e => setNameValue(e.target.value)} data-1p-ignore="true" data-lpignore="true" ref={nameRef} onKeyDown={handleEnter} onFocus={handleFocus} onBlur={handleBlur} /> : <StyledHeadingText id="event-name-field" level={2} tabIndex={0} isHovered={isHovered} onKeyDown={handleKeyDown} color={isHovered ? colorTextAction : null}>
            {nameValue}
            {secret && <SecretIcon />}
          </StyledHeadingText>}
      </InputContainer>
      <FieldError text={nameError} />
    </Wrapper>;
};

require("./editable_event_type.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./editable_event_type.js");